// Ícone e tom dos KPIs saem de `VOCABULARIO_KPI` (`src/auditoria/components/ds`).

import type { SxProps } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';

import useSWR from 'swr';
import { useMemo, useState } from 'react';

import Box from '@mui/material/Box';

import { fetcher } from 'src/lib/axios';
import { DashboardContent } from 'src/layouts/dashboard/content';
import { abrirModal } from 'src/auditoria/utils/abrir-modal';
import { TxDataGrid } from 'src/auditoria/components/tx-data-grid';
import {
  DsNum,
  DsButton,
  DsKpiCard,
  GradePager,
  GradeCanvas,
  DsPageHeader,
  DsEmptyState,
  DsStatusChip,
  SX_GRADE_CANVAS,
} from 'src/auditoria/components/ds';

import BoletoModal from './BoletoModal';
import { formatDateBr, formatCurrency } from '../Nfse/types';

// ----------------------------------------------------------------------
// Guias → Extrato de Débitos (portal SN). Débitos em aberto com juros/multa/total
// já calculados por public.extratodebito (mesma do Maker). Backend GET /guias.
//
// Canvas (portal-guias.dc.html): cabeçalho + 4 KPIs em grid 1fr×4 com border-left
// de 3px e rótulo ACIMA do número (`order="title-first"`, `scale="sans28"`), e um
// único card de grade com as colunas Guia · Referência · Vencimento · Valor ·
// Situação · ação. O hero de "Total a pagar" e os dois blocos-resumo da versão
// anterior sumiram: o artboard resolve os dois com a faixa de KPIs.
// ----------------------------------------------------------------------

// O `TxDataGrid` do portal declara `sx?: SxProps` (tema default do MUI) enquanto o
// kit tipa `SxProps<Theme>` do tema do app. Mesma forma, tipos nominalmente
// distintos: o estreitamento acontece uma vez aqui, sem `any` e sem `@ts-ignore`.
const { sx: SX_GRADE_KIT, ...PROPS_GRADE } = SX_GRADE_CANVAS;
const SX_GRADE = SX_GRADE_KIT as SxProps;

interface Debito {
  id: string;
  receita: string | null;
  exercicio: string | null;
  parcela: number | null;
  valor_lancado: number;
  vencimento: string | null;
  juros: number;
  multa: number;
  descontos: number;
  total: number;
  status: number | null;
}
interface Resumo {
  quantidade: number;
  valor_lancado: number;
  juros: number;
  multa: number;
  descontos: number;
  total: number;
}
interface GuiasResponse {
  debitos: Debito[];
  resumo: Resumo;
}

/** O canvas nunca mostra mais de 6 linhas; o rodapé só aparece quando sobra página. */
const TAMANHO_PAGINA = 10;

function hoje(): number {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}
function vencido(v: string | null): boolean {
  if (!v) return false;
  const t = new Date(v).getTime();
  return Number.isFinite(t) && t < hoje();
}

export default function GuiasListPage() {
  const { data, isLoading } = useSWR<GuiasResponse>('/portal-api/guias', fetcher);
  const [pagina, setPagina] = useState(0);

  const debitos = useMemo(() => data?.debitos ?? [], [data]);
  const resumo = data?.resumo;

  // Agregações client-side dos débitos em aberto, na ordem dos 4 KPIs do canvas.
  const debitosVencidos = debitos.filter((d) => vencido(d.vencimento));
  const totalVencido = debitosVencidos.reduce((acc, d) => acc + (d.total ?? 0), 0);
  const aVencer = debitos.length - debitosVencidos.length;
  const proximoVencimento =
    debitos
      .map((d) => d.vencimento)
      .filter((v): v is string => Boolean(v) && !vencido(v))
      .sort()[0] ?? null;
  const acrescimosTotal = (resumo?.juros ?? 0) + (resumo?.multa ?? 0) - (resumo?.descontos ?? 0);

  const paginaDebitos = debitos.slice(pagina * TAMANHO_PAGINA, (pagina + 1) * TAMANHO_PAGINA);
  const temPaginacao = debitos.length > TAMANHO_PAGINA;
  const vazio = !isLoading && debitos.length === 0;

  const columns: GridColDef<Debito>[] = [
    {
      field: 'id',
      headerName: 'Guia',
      width: 130,
      renderCell: (p) => <DsNum mono>{p.row.id}</DsNum>,
    },
    {
      field: 'referencia',
      headerName: 'Referência',
      flex: 1,
      minWidth: 200,
      valueGetter: (_v, r) =>
        [r.receita ?? '—', r.exercicio, r.parcela ? `parcela ${r.parcela}` : null]
          .filter(Boolean)
          .join(' · '),
    },
    {
      field: 'vencimento',
      headerName: 'Vencimento',
      width: 130,
      renderCell: (p) => <DsNum mono>{formatDateBr(p.row.vencimento)}</DsNum>,
    },
    {
      field: 'total',
      headerName: 'Valor (R$)',
      width: 130,
      align: 'right',
      headerAlign: 'right',
      renderCell: (p) => (
        <DsNum mono align="right" sx={{ fontWeight: 700 }}>
          {formatCurrency(p.row.total)}
        </DsNum>
      ),
    },
    {
      field: 'situacao',
      headerName: 'Situação',
      width: 120,
      sortable: false,
      renderCell: (p) =>
        vencido(p.row.vencimento) ? (
          <DsStatusChip tone="error">Vencida</DsStatusChip>
        ) : (
          <DsStatusChip tone="info">A vencer</DsStatusChip>
        ),
    },
    {
      field: 'acoes',
      headerName: '',
      width: 140,
      sortable: false,
      filterable: false,
      align: 'right',
      renderCell: (p) => (
        <DsButton
          tone="neutral"
          sx={{ padding: '6px 12px', fontSize: 12 }}
          onClick={() => abrirModal(<BoletoModal debitoId={p.row.id} />, 'Guia de pagamento', 'sm')}
        >
          Imprimir
        </DsButton>
      ),
    },
  ];

  return (
    <DashboardContent>
      <DsPageHeader
        weight={800}
        title="Guias"
        subtitle="Débitos em aberto, vencidas e pagas · emissão e impressão de boletos."
      />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: '1fr 1fr',
            md: 'repeat(4, 1fr)',
          },
          gap: '12px',
          mb: '12px',
        }}
      >
        <DsKpiCard
          scale="sans28"
          order="title-first"
          title="Em aberto"
          total={<DsNum>{formatCurrency(resumo?.total ?? 0)}</DsNum>}
          sub={`${aVencer} guia(s) a vencer`}
          conceito="dinheiro-em-aberto"
          borderLeft
        />
        <DsKpiCard
          scale="sans28"
          order="title-first"
          title="Vencidas"
          total={<DsNum>{debitosVencidos.length}</DsNum>}
          sub={`${formatCurrency(totalVencido)} em atraso`}
          conceito="vencido"
          borderLeft
        />
        <DsKpiCard
          scale="sans28"
          order="title-first"
          title="Valor original"
          total={<DsNum>{formatCurrency(resumo?.valor_lancado ?? 0)}</DsNum>}
          sub={`${formatCurrency(acrescimosTotal)} de juros, multa e descontos`}
          conceito="dinheiro-neutro"
          borderLeft
        />
        <DsKpiCard
          scale="sans28"
          order="title-first"
          title="Próximo vencimento"
          total={<DsNum>{proximoVencimento ? formatDateBr(proximoVencimento) : '—'}</DsNum>}
          sub={`${resumo?.quantidade ?? debitos.length} débito(s) em aberto`}
          conceito="data-vencimento"
          borderLeft
        />
      </Box>

      <GradeCanvas
        pager={
          temPaginacao && !vazio ? (
            <GradePager
              de={pagina * TAMANHO_PAGINA + 1}
              ate={Math.min((pagina + 1) * TAMANHO_PAGINA, debitos.length)}
              total={debitos.length}
              onPrev={() => setPagina((p) => Math.max(0, p - 1))}
              onNext={() => setPagina((p) => p + 1)}
              prevDisabled={pagina === 0}
              nextDisabled={(pagina + 1) * TAMANHO_PAGINA >= debitos.length}
            />
          ) : undefined
        }
      >
        {vazio ? (
          // `EmptyContent` (a imagem de 160px do TxEmpty) é proibido dentro da
          // grade pelo §4.5: o vazio do canvas é este bloco centralizado.
          <DsEmptyState
            icon="check"
            title="Nenhum débito em aberto"
            description="Você está em dia com o município. Novas guias aparecem aqui assim que forem lançadas."
          />
        ) : (
          <TxDataGrid<Debito>
            rows={paginaDebitos}
            columns={columns}
            loading={isLoading}
            emptyLabel="Nenhum débito em aberto"
            initialPageSize={TAMANHO_PAGINA}
            hideFooter
            {...PROPS_GRADE}
            sx={SX_GRADE}
          />
        )}
      </GradeCanvas>
    </DashboardContent>
  );
}
